import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Usage in routes:
 *   canActivate: [authGuard, roleGuard],
 *   data: { roles: ['Doctor', 'Admin'] }
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const requiredRoles: string[] = route.data['roles'] ?? [];
  if (requiredRoles.length === 0) return true;

  if (auth.hasAnyRole(...requiredRoles)) {
    return true;
  }

  return router.createUrlTree(['/dashboard']);
};

/**
 * Usage in routes:
 *   canActivate: [authGuard, permissionGuard],
 *   data: { permission: { module: 'billing', action: 'view', subscriptionKey: 'AccountAccess' } }
 *
 * `subscriptionKey` is optional — only set it on routes with a confirmed matching key in the
 * backend's Subscription.json feature list (see .claude/rules/permissions.md). When present, the
 * route also requires that subscription-plan feature to be included in the company's plan
 * (api/User/GetUserAccessDetails `SubscriptionAccesses`), on top of the role permission check.
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const config = route.data['permission'] as
    | { module: string; action?: 'view' | 'create' | 'edit' | 'delete' | 'export' | 'import'; subscriptionKey?: string }
    | undefined;

  if (!config) return true;

  // Grace period: api/Role/GetByUserId hasn't resolved yet for this session (e.g. right after
  // login/session-restore) — every moduleKey would otherwise look denied against the empty []
  // seed. Let navigation through rather than bouncing the user before we actually know the answer.
  // SubscriptionAccesses arrives in the same response, so this covers both checks below.
  if (!auth.permissionsLoaded()) return true;

  if (!auth.hasPermission(config.module, config.action ?? 'view')) {
    return router.createUrlTree(['/access-denied']);
  }

  if (config.subscriptionKey && !auth.canAccessMenu(config.subscriptionKey)) {
    return router.createUrlTree(['/access-denied']);
  }

  return true;
};
